import { useTranslation } from '../../../i18n/i18n';
import { BALES_UNIT, NUMERIC_COMPARISON_EPSILON, formatMeasureLabel } from '../../../utils/recipes/numberFormatUtils';
import { cn } from '../../../utils/cn';
import EllipseTooltip from '../../../utils/EllipseTooltip';

const ROW_GRID = 'grid grid-cols-[1fr_100px_100px_100px] gap-2.5 items-center';

// Totals label per unit: tonnes weigh, bales count; ratios fall back to the plain total.
const TOTAL_LABEL_KEY_BY_UNIT = {
  t: 'production_corrections_total_weight',
  [BALES_UNIT]: 'production_corrections_total_bales',
};

function MaterialRow({ material, index, unit, languageCode, t }) {
  const prev = material?.previousValue ?? 0;
  const next = material?.newValue ?? prev;
  const diff = material?.delta ?? next - prev;
  const isNew = prev < NUMERIC_COMPARISON_EPSILON && next > NUMERIC_COMPARISON_EPSILON;
  const isCorrected = material?.corrected === true || Math.abs(diff) > NUMERIC_COMPARISON_EPSILON;
  const isRemoved = next < NUMERIC_COMPARISON_EPSILON && isCorrected && !isNew;
  const name = material?.name || `${t('common_unknown')} #${index + 1}`;
  const color = material?.background_color || '#6b7280';

  const changeText =
    Math.abs(diff) > NUMERIC_COMPARISON_EPSILON
      ? `${diff > 0 ? '+' : ''}${formatMeasureLabel(diff, unit, languageCode, t)}`
      : '-';
  const changeClass =
    diff > NUMERIC_COMPARISON_EPSILON
      ? 'text-[#007E24] dark:text-[#03CA3C]'
      : diff < -NUMERIC_COMPARISON_EPSILON
        ? 'text-[#CE0000] dark:text-[#FF6D6D]'
        : 'text-[#0F172A] dark:text-[#8A8C95]';

  return (
    <div className={cn(ROW_GRID, 'text-sm')}>
      {/* Name + badges */}
      <div className='flex min-w-0 items-center gap-2'>
        <span className='size-2.5 shrink-0 rounded-sm' style={{ backgroundColor: color }} />
        <span
          className={cn(
            'min-w-0 truncate text-base leading-[140%] font-medium text-[#464255] dark:text-[#FAFAFA]',
            isRemoved && 'line-through text-[#64748B] dark:text-[#CAD5E2]',
          )}
        >
          <EllipseTooltip title={name}>{name}</EllipseTooltip>
        </span>
        {isCorrected && !isNew && (
          <span className='shrink-0 text-sm leading-4 font-medium text-[#A65300] dark:text-[#FFB061] px-1 py-0.5 rounded bg-[#F1EAE6] dark:bg-[#412000] '>
            {t('production_corrections_corrected')}
          </span>
        )}
        {isNew && (
          <span className='shrink-0 text-sm leading-4 font-medium text-[#1355E5] dark:text-[#8EB1FF] px-1 py-0.5 rounded bg-[#DFE9FD] dark:bg-[#001D56]'>
            {t('production_corrections_new')}
          </span>
        )}
      </div>

      {/* Current */}
      <div className='text-center tabular-nums text-base leading-[120%] font-semibold text-[#737580] dark:text-[#CAD5E2]'>
        {prev < NUMERIC_COMPARISON_EPSILON ? '-' : formatMeasureLabel(prev, unit, languageCode, t)}
      </div>

      {/* Change */}
      <div className={cn('text-center tabular-nums text-base leading-[120%] font-bold', changeClass)}>{changeText}</div>

      {/* Updated */}
      <div className='text-center tabular-nums text-base leading-[120%] font-bold text-[#0F172A] dark:text-[#FAFAFA]'>
        {next < NUMERIC_COMPARISON_EPSILON ? '-' : formatMeasureLabel(next, unit, languageCode, t)}
      </div>
    </div>
  );
}

function TotalsRow({ materials, unit, languageCode, t }) {
  let currentTotal = 0;
  let updatedTotal = 0;
  materials.forEach((material) => {
    const prev = material?.previousValue ?? 0;
    const next = material?.newValue ?? prev;
    currentTotal += prev;
    updatedTotal += next;
  });
  const changeTotal = updatedTotal - currentTotal;

  return (
    <>
      <div className='mt-4 h-px w-full bg-[#DDE6F9] dark:bg-[#3C3E53]' />
      <div className={cn('mt-2.5', ROW_GRID, 'text-sm font-semibold')}>
        <span className='text-sm leading-[120%] font-semibold text-[#737580] dark:text-[#90A1B9]'>
          {t(TOTAL_LABEL_KEY_BY_UNIT[unit] ?? 'production_corrections_total')}
        </span>
        <span className='text-center tabular-nums text-lg leading-[120%] font-semibold text-[#737580] dark:text-[#CAD5E2]'>
          {formatMeasureLabel(currentTotal, unit, languageCode, t)}
        </span>
        <span
          className={cn(
            'text-center tabular-nums text-lg leading-[120%] font-bold',
            changeTotal > NUMERIC_COMPARISON_EPSILON
              ? 'text-[#007E24] dark:text-[#03CA3C]'
              : changeTotal < -NUMERIC_COMPARISON_EPSILON
                ? 'text-[#CE0000] dark:text-[#FF6D6D]'
                : 'text-[#0F172A] dark:text-[#8A8C95]',
          )}
        >
          {changeTotal > NUMERIC_COMPARISON_EPSILON ? '+' : ''}
          {formatMeasureLabel(changeTotal, unit, languageCode, t)}
        </span>
        <span className='text-center tabular-nums text-lg leading-[120%] font-bold text-[#2563EB] dark:text-[#3D8DD4]'>
          {formatMeasureLabel(updatedTotal, unit, languageCode, t)}
        </span>
      </div>
    </>
  );
}

/**
 * Renders a list of correction materials. Materials are expected to be already
 * normalized (see recipeNormalization): each carries value/unit/previousValue/
 * newValue/delta/corrected, so this component only formats and displays them.
 *
 * A correction spanning more than one conveyor renders a section per conveyor (`deviceGroups`),
 * each with its own totals — a total across conveyors is meaningless for ratios. Anything
 * else keeps the plain list built from `materials`.
 */
export default function MaterialCorrectionList({ materials = [], deviceGroups = [], deviceNames = {}, languageCode }) {
  const { t } = useTranslation(languageCode);
  const correctionMaterials = Array.isArray(materials) ? materials : [];
  const groups = Array.isArray(deviceGroups) ? deviceGroups : [];

  // All materials in a correction share a production type, so a single unit drives the table.
  const unit = correctionMaterials[0]?.unit ?? groups[0]?.materials?.[0]?.unit ?? 't';

  // A null deviceId marks the ungrouped section, which renders without a conveyor header.
  const sections = groups.length > 1 ? groups : [{ deviceId: null, materials: correctionMaterials }];

  const resolveDeviceLabel = (deviceId) =>
    deviceId ? (deviceNames?.[deviceId]?.display_name ?? deviceId) : t('production_corrections_all_conveyors');

  return (
    <div className='rounded-lg bg-[#F3F7FF] border border-[#DDE6F9] dark:border-none dark:bg-[#2E3440] overflow-hidden'>
      {/* Header */}
      <div className='p-4 pb-0'>
        <div className={ROW_GRID}>
          <span className='text-sm leading-[120%] font-semibold text-[#0F172A] dark:text-[#FAFAFA]'>
            {t('production_corrections_ingredients')}
          </span>
          <span className='text-center text-sm leading-[120%] font-medium uppercase text-[#464255] dark:text-[#90A1B9]'>
            {t('production_corrections_current')}
          </span>
          <span className='text-center text-sm leading-[120%] font-medium uppercase text-[#464255] dark:text-[#90A1B9]'>
            {t('production_corrections_changes')}
          </span>
          <span className='text-center text-sm leading-[120%] font-medium uppercase text-[#464255] dark:text-[#90A1B9]'>
            {t('production_corrections_updated')}
          </span>
        </div>
        <div className='mt-2.5 h-px w-full bg-[#DDE6F9] dark:bg-[#3C3E53]' />
      </div>

      {/* Rows */}
      <div className='px-4 pb-4'>
        {sections.map((section) => (
          <div
            key={section.deviceId ?? 'ungrouped'}
            className='mt-4 pt-4 border-t border-[#DDE6F9] dark:border-[#90A1B9] first:mt-0 first:pt-0 first:border-t-0'
          >
            {section.deviceId !== null && (
              <div className='mt-3 text-sm leading-[120%] font-semibold uppercase text-[#0F172A] dark:text-[#FAFAFA]'>
                {resolveDeviceLabel(section.deviceId)}
              </div>
            )}

            <div className='space-y-3 mt-3'>
              {section.materials.map((material, index) => (
                <MaterialRow
                  key={`${section.deviceId ?? 'ungrouped'}-${material?.ingredientId || 'material'}-${index}`}
                  material={material}
                  index={index}
                  unit={unit}
                  languageCode={languageCode}
                  t={t}
                />
              ))}
            </div>

            {section.materials.length > 0 && (
              <TotalsRow materials={section.materials} unit={unit} languageCode={languageCode} t={t} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
